/**
 * Session Score View Service
 *
 * Application service that loads and normalizes the score payload required by
 * the simulation score-view feature.
 *
 * Why this service exists
 * - Keeps feature hooks focused on orchestration and UI state transitions.
 * - Centralizes scorecard mapping strategy in a reusable, testable boundary.
 * - Provides a stable return shape to callers regardless of API payload details.
 */
import { getScoreCardBySession } from '@/lib';
import { mapApiScorecardToFrontend } from '@/utils';

/**
 * Stable output contract for session score view hydration.
 *
 * Notes
 * - `createdAt` is the API timestamp surfaced in the score view metadata.
 * - `score` is the normalized frontend model produced by
 *   `mapApiScorecardToFrontend`.
 */
export interface SessionScoreViewData {
  /** Raw creation timestamp from backend scorecard payload. */
  createdAt: string;
  /** Canonical frontend score model used by store/view layers. */
  score: ReturnType<typeof mapApiScorecardToFrontend>;
}

/**
 * Load and normalize score view data for a specific simulation session.
 *
 * Execution flow
 * 1. Fetch raw scorecard payload by session id.
 * 2. Map payload into frontend score model with explicit field strategies.
 * 3. Return only the values required by the consuming feature hook.
 *
 * Mapping strategy rationale
 * - `certificationId`: sourced from nested score payload when available.
 * - `dateCompletedStrategy`: use score payload date as primary source.
 * - `passStrategy`: rely on certification pass semantics from payload.
 * - `simulationTypeStrategy`: resolve from score payload context.
 * - `uuidStrategy`: use score id convention for stable identity.
 * - `fallbackStrategy`: preserve raw fallback behavior for compatibility.
 *
 * @param sessionId Numeric session id from route/query context.
 * @returns Minimal, stable session score view data.
 */
export const loadSessionScoreView = async (sessionId: number): Promise<SessionScoreViewData> => {
  // ================================
  // Step 1: Fetch Raw Scorecard
  // ================================
  // This request is the single source of truth for score-view hydration.
  const apiScorecard = await getScoreCardBySession(sessionId);

  // ================================
  // Step 2: Normalize to Frontend Model
  // ================================
  // Strategy options are explicitly pinned here so downstream callers get
  // consistent score identity and pass/date resolution behavior.
  const score = mapApiScorecardToFrontend(apiScorecard, {
    certificationId: apiScorecard.certification_id?.toString(),
    dateCompletedStrategy: 'score-payload',
    fallbackStrategy: 'raw',
    passStrategy: 'cert-pass',
    simulationTypeStrategy: 'score-payload',
    uuidStrategy: 'score-id',
  });

  // ================================
  // Step 3: Return Feature Contract
  // ================================
  // The service returns only what the feature needs for store updates and UI metadata.
  return {
    createdAt: apiScorecard.date_completed ?? '',
    score,
  };
};
